import axios from 'axios';
import { toast } from 'react-toastify';

// ── Action Types ──────────────────────────────────────────────
export const STRIPE_CONNECTING = 'STRIPE_CONNECTING';
export const STRIPE_CONNECT_SUCCESS = 'STRIPE_CONNECT_SUCCESS';
export const STRIPE_CONNECT_FAILURE = 'STRIPE_CONNECT_FAILURE';
export const STRIPE_STATUS_RECEIVED = 'STRIPE_STATUS_RECEIVED';
export const STRIPE_STATUS_RECEIVING = 'STRIPE_STATUS_RECEIVING';
export const STRIPE_DISCONNECT_SUCCESS = 'STRIPE_DISCONNECT_SUCCESS';
export const STRIPE_DASHBOARD_LINK_RECEIVED = 'STRIPE_DASHBOARD_LINK_RECEIVED';
export const STRIPE_TIP_HISTORY_RECEIVED = 'STRIPE_TIP_HISTORY_RECEIVED';
export const STRIPE_TIP_HISTORY_RECEIVING = 'STRIPE_TIP_HISTORY_RECEIVING';
export const STRIPE_LOCATIONS_RECEIVING = 'STRIPE_LOCATIONS_RECEIVING';
export const STRIPE_LOCATIONS_RECEIVED = 'STRIPE_LOCATIONS_RECEIVED';
export const STRIPE_LOCATIONS_FAILURE = 'STRIPE_LOCATIONS_FAILURE';
export const STRIPE_READER_REGISTERING = 'STRIPE_READER_REGISTERING';
export const STRIPE_READER_REGISTERED = 'STRIPE_READER_REGISTERED';
export const STRIPE_READER_REGISTER_FAILURE = 'STRIPE_READER_REGISTER_FAILURE';

// ── Stripe onboarding URLs for employees ──────────────────────
// return_url / refresh_url must be distinct URLs we control.
// return_url → completion page (status sync)
// refresh_url → refreshes an expired AccountLink and redirects back to Stripe
function getStripeOnboardingReturnUrl(userId) {
    return window.location.origin + '/stripe-onboarding-complete.html?userId=' + encodeURIComponent(userId);
}

function getStripeOnboardingRefreshUrl(userId) {
    return window.location.origin + '/stripe-onboarding-refresh.html?userId=' + encodeURIComponent(userId);
}

// ── Check Stripe Account Status ───────────────────────────────
// Polls the backend to check if user has completed onboarding
// and whether their connected account can receive transfers.
export function getStripeAccountStatusRequest(userId) {
    return (dispatch) => {
        dispatch({ type: STRIPE_STATUS_RECEIVING, payload: userId });

        axios.get(`/api/stripe/connect/status/${userId}`).then(res => {
            if (res.data.status === "Error") {
                dispatch({ type: STRIPE_CONNECT_FAILURE, payload: res.data.message });
            } else {
                dispatch({
                    type: STRIPE_STATUS_RECEIVED,
                    payload: {
                        userId,
                        stripeAccountId: res.data.stripeAccountId,
                        stripeAccountStatus: res.data.stripeAccountStatus,
                        tipPayoutEnabled: res.data.tipPayoutEnabled,
                        payoutsEnabled: res.data.payoutsEnabled,
                        chargesEnabled: res.data.chargesEnabled,
                        accountLinkSource: res.data.accountLinkSource
                    }
                });
            }
        }).catch(err => {
            dispatch({ type: STRIPE_CONNECT_FAILURE, payload: err.message });
        });
    };
}

export function linkExistingStripeAccountRequest(userId, stripeAccountId) {
    return (dispatch) => {
        dispatch({ type: STRIPE_CONNECTING, payload: userId });

        axios.post('/api/stripe/connect/link-existing', {
            UserId: userId,
            StripeAccountId: stripeAccountId
        }).then(res => {
            if (res.data.status === "Error") {
                toast.error(res.data.message || 'Failed to link Stripe account.');
                dispatch({ type: STRIPE_CONNECT_FAILURE, payload: res.data.message });
            } else {
                dispatch({
                    type: STRIPE_STATUS_RECEIVED,
                    payload: {
                        userId,
                        stripeAccountId: res.data.stripeAccountId,
                        stripeAccountStatus: res.data.stripeAccountStatus,
                        tipPayoutEnabled: res.data.tipPayoutEnabled,
                        payoutsEnabled: res.data.payoutsEnabled,
                        chargesEnabled: res.data.chargesEnabled,
                        accountLinkSource: res.data.accountLinkSource
                    }
                });
                toast.success('Existing Stripe account linked to employee.');
            }
        }).catch(err => {
            toast.error('Failed to link Stripe account.');
            dispatch({ type: STRIPE_CONNECT_FAILURE, payload: err.message });
        });
    };
}

// ── Disconnect Stripe Account ─────────────────────────────────
export function disconnectStripeAccountRequest(userId) {
    return (dispatch) => {
        dispatch({ type: STRIPE_CONNECTING, payload: userId });

        axios.post(`/api/stripe/connect/disconnect/${userId}`).then(res => {
            if (res.data.status === "Error") {
                toast.error(res.data.message);
                dispatch({ type: STRIPE_CONNECT_FAILURE, payload: res.data.message });
            } else {
                dispatch({
                    type: STRIPE_DISCONNECT_SUCCESS,
                    payload: { userId }
                });
                toast.success("Stripe account disconnected.");
            }
        }).catch(err => {
            toast.error("Failed to disconnect Stripe account.");
            dispatch({ type: STRIPE_CONNECT_FAILURE, payload: err.message });
        });
    };
}

// ── Get Stripe Express Dashboard Link ─────────────────────────
// Generates a one-time link for the user to access their
// Stripe Express dashboard (view payouts, tax forms, etc.)
export function getStripeDashboardLinkRequest(userId) {
    return (dispatch) => {
        axios.get(`/api/stripe/connect/dashboard-link/${userId}`).then(res => {
            if (res.data.status === "Error") {
                toast.error(res.data.message);
            } else {
                window.open(res.data.dashboardUrl, '_blank');
            }
        }).catch(err => {
            toast.error("Failed to open Stripe dashboard.");
        });
    };
}

// ── Fix Restricted / Incomplete Stripe Account ────────────────
// Uses Account Links type=account_update (preferred over LoginLink
// when Stripe still needs information from the employee).
export function getStripeAccountUpdateLinkRequest(userId) {
    return (dispatch) => {
        axios.post('/api/stripe/connect/account-update-link', {
            UserId: userId,
            ReturnUrl: getStripeOnboardingReturnUrl(userId),
            RefreshUrl: getStripeOnboardingRefreshUrl(userId)
        }).then(res => {
            if (res.data.status === "Error") {
                toast.error(res.data.message || 'Failed to open Stripe update link.');
            } else if (res.data.updateUrl) {
                window.open(res.data.updateUrl, '_blank');
            } else {
                toast.error('Stripe did not return an update link.');
            }
        }).catch(err => {
            toast.error("Failed to open Stripe update link.");
        });
    };
}

// ── Get Tip Payout History for a User ─────────────────────────
export function getTipPayoutHistoryRequest(userId, page = 1, limit = 20) {
    return (dispatch) => {
        dispatch({ type: STRIPE_TIP_HISTORY_RECEIVING });

        axios.get(`/api/stripe/tips/history/${userId}`, {
            params: { page, limit }
        }).then(res => {
            if (res.data.status === "Error") {
                toast.error(res.data.message);
            } else {
                dispatch({
                    type: STRIPE_TIP_HISTORY_RECEIVED,
                    payload: {
                        userId,
                        tips: res.data.tips,
                        totalAmount: res.data.totalAmount,
                        totalCount: res.data.totalCount,
                        page: res.data.page
                    }
                });
            }
        }).catch(err => {
            toast.error("Failed to load tip history.");
        });
    };
}

// ── Toggle Tip Payout for a User ──────────────────────────────
export function toggleTipPayoutRequest(userId, enabled) {
    return (dispatch) => {
        axios.put('/api/stripe/connect/toggle-payout', {
            UserId: userId,
            TipPayoutEnabled: enabled
        }).then(res => {
            if (res.data.status === "Error") {
                toast.error(res.data.message);
            } else {
                dispatch({
                    type: STRIPE_STATUS_RECEIVED,
                    payload: {
                        userId,
                        tipPayoutEnabled: enabled,
                        stripeAccountStatus: res.data.stripeAccountStatus
                    }
                });
                toast.success(`Tip payouts ${enabled ? 'enabled' : 'disabled'} for user.`);
            }
        }).catch(err => {
            toast.error("Failed to update tip payout setting.");
        });
    };
}

// ── Send Stripe Onboarding Link via Email ─────────────────────
// Employee receives link, completes onboarding, sees Stripe success page.
export function sendOnboardingLinkEmailRequest(userId, merchantId, email, username) {
    return (dispatch) => {
        dispatch({ type: STRIPE_CONNECTING, payload: userId });

        axios.post('/api/stripe/connect/send-onboarding-email', {
            UserId: userId,
            MerchantId: merchantId,
            Email: email,
            Username: username,
            ReturnUrl: getStripeOnboardingReturnUrl(userId),
            RefreshUrl: getStripeOnboardingRefreshUrl(userId)
        }).then(res => {
            if (res.data.status === "Error") {
                toast.error(res.data.message);
                dispatch({ type: STRIPE_CONNECT_FAILURE, payload: res.data.message });
            } else {
                dispatch({
                    type: STRIPE_CONNECT_SUCCESS,
                    payload: {
                        userId,
                        stripeAccountId: res.data.stripeAccountId,
                        onboardingUrl: res.data.onboardingUrl,
                        accountLinkSource: 'created_platform'
                    }
                });
                toast.success(`Stripe onboarding link sent to ${email}`);
            }
        }).catch(err => {
            toast.error("Failed to send onboarding email.");
            dispatch({ type: STRIPE_CONNECT_FAILURE, payload: err.message });
        });
    };
}

// ── Send Stripe Onboarding Link via SMS ───────────────────────
// Employee receives link, completes onboarding, sees Stripe success page.
export function sendOnboardingLinkSmsRequest(userId, merchantId, phone, username) {
    return (dispatch) => {
        dispatch({ type: STRIPE_CONNECTING, payload: userId });

        axios.post('/api/stripe/connect/send-onboarding-sms', {
            UserId: userId,
            MerchantId: merchantId,
            Phone: phone,
            Username: username,
            ReturnUrl: getStripeOnboardingReturnUrl(userId),
            RefreshUrl: getStripeOnboardingRefreshUrl(userId)
        }).then(res => {
            if (res.data.status === "Error") {
                toast.error(res.data.message);
                dispatch({ type: STRIPE_CONNECT_FAILURE, payload: res.data.message });
            } else {
                dispatch({
                    type: STRIPE_CONNECT_SUCCESS,
                    payload: {
                        userId,
                        stripeAccountId: res.data.stripeAccountId,
                        onboardingUrl: res.data.onboardingUrl,
                        accountLinkSource: 'created_platform'
                    }
                });
                toast.success(`Stripe onboarding link sent to ${phone}`);
            }
        }).catch(err => {
            toast.error("Failed to send onboarding SMS.");
            dispatch({ type: STRIPE_CONNECT_FAILURE, payload: err.message });
        });
    };
}

// ── Get Terminal Locations for a Connected Account ────────────
// Returns all Stripe Terminal locations attached to the given
// Stripe connected account (e.g. a merchant's acct_xxx).
export function getStripeTerminalLocationsRequest(stripeAccountId) {
    return (dispatch) => {
        dispatch({ type: STRIPE_LOCATIONS_RECEIVING });

        axios.get(`/api/stripe/terminal/locations/${stripeAccountId}`).then(res => {
            if (res.data.status === 'Error') {
                toast.error(res.data.message);
                dispatch({ type: STRIPE_LOCATIONS_FAILURE, payload: res.data.message });
            } else {
                dispatch({
                    type: STRIPE_LOCATIONS_RECEIVED,
                    payload: {
                        stripeAccountId,
                        locations: res.data.locations
                    }
                });
            }
        }).catch(err => {
            toast.error('Failed to load terminal locations.');
            dispatch({ type: STRIPE_LOCATIONS_FAILURE, payload: err.message });
        });
    };
}

// ── Register a Terminal Reader to a Connected Account ─────────
// Pairs a physical Stripe Terminal device with a location on the
// given connected account using the registration code shown on
// the reader's screen.
//
// payload: {
//   stripeAccountId : string  — the merchant's connected account (acct_xxx)
//   locationId      : string  — Stripe Terminal location ID (tml_xxx)
//   registrationCode: string  — pairing code displayed on the reader
//   label           : string  — optional human-readable name for the reader
// }
export function registerStripeTerminalReaderRequest(payload) {
    return (dispatch) => {
        dispatch({ type: STRIPE_READER_REGISTERING });

        axios.post('/api/stripe/terminal/readers', payload).then(res => {
            if (res.data.status === 'Error') {
                toast.error(res.data.message);
                dispatch({ type: STRIPE_READER_REGISTER_FAILURE, payload: res.data.message });
            } else {
                dispatch({
                    type: STRIPE_READER_REGISTERED,
                    payload: res.data.reader
                });
                toast.success(`Terminal "${res.data.reader.label || res.data.reader.id}" registered successfully.`);
            }
        }).catch(err => {
            toast.error('Failed to register terminal reader.');
            dispatch({ type: STRIPE_READER_REGISTER_FAILURE, payload: err.message });
        });
    };
}
